import { Box, Text, IconButton, ContentCopyIcon, CheckIcon } from '@citibox-panel/ui';
import { useCopiedFlag } from '../../../lib/useCopiedFlag';

export interface ObservationsBlockProps {
  observations: string;
}

// The account's trail, framed and copyable in one gesture: the baja trail of a «Usuario», and on a
// courier account the whole history — old notes plus one trace line per action. The agent pastes
// it into Zendesk whole, so what is copied is the raw string, unformatted and untrimmed.
//
// On screen the line breaks are kept (`pre-line`: breaks honoured, repeated spaces folded, as
// everywhere else on the ficha). CSS breaks on a line feed only, and the admin's bulk deactivation
// separates its notes with a bare carriage return, so those become line feeds before painting —
// never in the copy.
//
// The value is backend/third-party content and is treated as such: it renders as a React text node
// (escaped, inert) and never as HTML — no dangerouslySetInnerHTML, no markdown, no linkifying, no
// matter how it is escaped upstream. It is also never logged and never sent to analytics: it is
// free text about one «Usuario» and can carry anything an operator once typed into it.
export function ObservationsBlock({ observations }: ObservationsBlockProps) {
  const { copied, copy } = useCopiedFlag();
  const onScreen = observations.replace(/\r\n?/g, '\n');

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        p: 1.5,
        display: 'flex',
        gap: 1,
        // The trail is long and the button is one line tall: top-aligned, it stays beside the
        // FIRST line instead of drifting to the vertical middle of a paragraph.
        alignItems: 'flex-start',
      }}
    >
      {/* minWidth: 0 lets this flex child shrink below its content width — without it a long
          unbroken trail pushes the button out of the frame instead of wrapping. */}
      <Box sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere', whiteSpace: 'pre-line' }}>
        <Text variant="body2">{onScreen}</Text>
      </Box>
      {/* TODO: i18n */}
      <IconButton
        size="small"
        aria-label="Copiar observaciones"
        onClick={() => {
          void copy(observations);
        }}
      >
        {copied ? <CheckIcon color="success" /> : <ContentCopyIcon />}
      </IconButton>
      {/* Visually hidden, never unmounted. The glyph swap is the confirmation for the agent who can
          see it; this region is the other half of it. Mounted empty from the start — a live region
          that appears together with its message is announced unreliably — and clipped rather than
          display:none, which would take it out of the accessibility tree and the announcement with
          it. The button's own name stays «Copiar observaciones» throughout, so the control is not
          renamed underneath a screen reader mid-interaction. */}
      <Box
        role="status"
        sx={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          clipPath: 'inset(50%)',
          whiteSpace: 'nowrap',
        }}
      >
        {/* TODO: i18n */}
        {copied ? 'Observaciones copiadas' : ''}
      </Box>
    </Box>
  );
}
